import React, { useState, useEffect } from 'react';
import { ipdAPI } from '../../utils/api';
import { toast } from 'react-toastify';

const WARD_TYPES = ['General', 'Medical', 'Surgical', 'ICU', 'CCU', 'Pediatric', 'Maternity', 'Ortho', 'Psychiatric', 'Burns', 'Private'];
const BED_STATUS_COLOR = { Available: '#16a34a', Occupied: '#dc2626', Reserved: '#d97706', Maintenance: '#64748b' };
const BED_STATUS_BG = { Available: '#f0fdf4', Occupied: '#fef2f2', Reserved: '#fffbeb', Maintenance: '#f8fafc' };

const WARD_TYPE_ICON = {
    General: '🏥', Medical: '💊', Surgical: '🩻', ICU: '🩺', CCU: '💗',
    Pediatric: '👶', Maternity: '🤱', Ortho: '🦴', Psychiatric: '🧠', Burns: '🩹', Private: '⭐'
};

const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };

export default function WardManagement() {
    const [wards, setWards] = useState([]);
    const [beds, setBeds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedWard, setExpandedWard] = useState(null);
    const [saving, setSaving] = useState(false);
    const [showAddWard, setShowAddWard] = useState(false);
    const [wardForm, setWardForm] = useState({ name: '', type: 'General' });
    const [bedInputs, setBedInputs] = useState([]);

    const load = async () => {
        setLoading(true);
        try {
            const [wardsRes, bedsRes] = await Promise.all([ipdAPI.getWards(), ipdAPI.getAllBeds()]);
            setWards(wardsRes.data);
            setBeds(bedsRes.data);
        } catch { toast.error('Failed to load ward data'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const wardBeds = (wardId) => beds.filter(b => b.ward_id === wardId);

    const totalStats = {
        total: beds.length,
        available: beds.filter(b => b.status === 'Available').length,
        occupied: beds.filter(b => b.status === 'Occupied').length,
    };

    const createWard = async () => {
        if (!wardForm.name.trim()) return toast.error('Ward name required');
        setSaving(true);
        try {
            await ipdAPI.createWard(wardForm);
            toast.success('Ward created');
            setWardForm({ name: '', type: 'General' });
            setShowAddWard(false);
            load();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
        finally { setSaving(false); }
    };

    const addBed = async (wardId) => {
        const bed_number = bedInputs[wardId]?.trim();
        if (!bed_number) return toast.error('Enter bed number');
        setSaving(true);
        try {
            await ipdAPI.addBed({ ward_id: wardId, bed_number });
            toast.success('Bed added');
            setBedInputs(p => ({ ...p, [wardId]: '' }));
            load();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
        finally { setSaving(false); }
    };

    const deleteWard = async (ward) => {
        if (!window.confirm(`Delete "${ward.name}"? All empty beds will be removed.`)) return;
        try {
            await ipdAPI.deleteWard(ward.id);
            toast.success('Ward deleted');
            if (expandedWard === ward.id) setExpandedWard(null);
            load();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    };

    if (loading) return <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>Loading...</div>;

    return (
        <div style={{ maxWidth: 900 }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 28 }}>🏥</span>
                    <div>
                        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1e3a5f', margin: 0 }}>Ward & Bed Management</h2>
                        <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>Manage wards and beds</p>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => setShowAddWard(!showAddWard)}
                        style={{ padding: '8px 14px', background: '#0f766e', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                        + Add Ward
                    </button>
                    <button onClick={load} style={{ padding: '8px 10px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>🔄</button>
                </div>
            </div>

            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 20 }}>
                {[
                    { label: 'Total Beds', value: totalStats.total, color: '#0369a1', bg: '#eff6ff' },
                    { label: 'Available', value: totalStats.available, color: '#16a34a', bg: '#f0fdf4' },
                    { label: 'Occupied', value: totalStats.occupied, color: '#dc2626', bg: '#fef2f2' },
                    { label: 'Occupancy', value: totalStats.total ? Math.round((totalStats.occupied / totalStats.total) * 100) + '%' : '0%', color: '#7c3aed', bg: '#f5f3ff' },
                ].map(s => (
                    <div key={s.label} style={{ background: s.bg, borderRadius: 10, padding: '12px 16px', textAlign: 'center', border: `1px solid ${s.color}25` }}>
                        <div style={{ fontSize: 22, fontWeight: 900, color: s.color }}>{s.value}</div>
                        <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>{s.label}</div>
                    </div>
                ))}
            </div>

            {/* Add Ward Form */}
            {showAddWard && (
                <div style={{ background: 'white', borderRadius: 10, padding: 18, marginBottom: 16, border: '1px solid #99f6e4' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0f766e', marginBottom: 14 }}>New Ward</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10, alignItems: 'flex-end' }}>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Ward Name *</label>
                            <input style={inputStyle} value={wardForm.name} onChange={e => setWardForm(f => ({ ...f, name: e.target.value }))}
                                placeholder="e.g. Male Medical Ward A" />
                        </div>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Ward Type *</label>
                            <select style={{ ...inputStyle, background: 'white' }} value={wardForm.type} onChange={e => setWardForm(f => ({ ...f, type: e.target.value }))}>
                                {WARD_TYPES.map(t => <option key={t}>{t}</option>)}
                            </select>
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                            <button onClick={createWard} disabled={saving}
                                style={{ padding: '8px 18px', background: saving ? '#9ca3af' : '#0f766e', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
                                {saving ? '...' : 'Create'}
                            </button>
                            <button onClick={() => setShowAddWard(false)}
                                style={{ padding: '8px 12px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>✕</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Ward Accordion List */}
            {wards.length === 0 ? (
                <div style={{ background: 'white', borderRadius: 10, padding: 50, textAlign: 'center', color: '#64748b', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: 40, marginBottom: 10 }}>🏥</div>
                    <div style={{ fontWeight: 600 }}>No wards yet</div>
                    <button onClick={() => setShowAddWard(true)} style={{ marginTop: 14, padding: '9px 22px', background: '#0f766e', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                        + Create First Ward
                    </button>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {wards.map(ward => {
                        const wBeds = wardBeds(ward.id);
                        const available = wBeds.filter(b => b.status === 'Available').length;
                        const occupied = wBeds.filter(b => b.status === 'Occupied').length;
                        const pct = wBeds.length ? Math.round((occupied / wBeds.length) * 100) : 0;
                        const isOpen = expandedWard === ward.id;

                        return (
                            <div key={ward.id} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                                {/* Ward Header Row — clickable */}
                                <div onClick={() => setExpandedWard(isOpen ? null : ward.id)}
                                    style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '13px 18px', cursor: 'pointer', background: isOpen ? '#f8fafc' : 'white', transition: 'background 0.15s' }}>
                                    <span style={{ fontSize: 22, flexShrink: 0 }}>{WARD_TYPE_ICON[ward.type] || '🏥'}</span>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>{ward.name}</div>
                                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{ward.type} Ward</div>
                                    </div>

                                    {/* Bed counts */}
                                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                        <span style={{ background: '#f0fdf4', color: '#16a34a', borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 700 }}>{available} available</span>
                                        <span style={{ background: '#fef2f2', color: '#dc2626', borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 700 }}>{occupied} occupied</span>
                                        <span style={{ background: '#f1f5f9', color: '#64748b', borderRadius: 20, padding: '3px 12px', fontSize: 12 }}>{wBeds.length} total</span>
                                    </div>

                                    {/* Progress bar */}
                                    <div style={{ width: 80, height: 6, background: '#f1f5f9', borderRadius: 3, flexShrink: 0 }}>
                                        <div style={{ width: `${pct}%`, height: '100%', background: pct > 80 ? '#dc2626' : pct > 60 ? '#d97706' : '#16a34a', borderRadius: 3 }} />
                                    </div>
                                    <span style={{ fontSize: 11, color: '#94a3b8', minWidth: 36 }}>{pct}%</span>

                                    {/* Rate per day */}
                                    <div onClick={e => e.stopPropagation()} style={{ display:'flex', alignItems:'center', gap:4, flexShrink:0 }}>
                                        <span style={{ fontSize:11, color:'#64748b' }}>Rs.</span>
                                        <input
                                            type="number" min="0"
                                            defaultValue={ward.rate_per_day || 0}
                                            onBlur={async e => {
                                                const rate = parseFloat(e.target.value) || 0;
                                                try { await ipdAPI.updateWardRate(ward.id, rate); toast.success(`Rate updated: Rs.${rate}/day`); }
                                                catch { toast.error('Failed to update rate'); }
                                            }}
                                            style={{ width:70, padding:'3px 6px', border:'1px solid #d1d5db', borderRadius:5, fontSize:12, textAlign:'right' }}
                                            title="Rate per day"
                                        />
                                        <span style={{ fontSize:11, color:'#64748b' }}>/day</span>
                                    </div>

                                    {/* Delete */}
                                    <button onClick={e => { e.stopPropagation(); deleteWard(ward); }}
                                        title="Delete ward"
                                        style={{ background: 'none', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer', color: '#dc2626', fontSize: 14, padding: '3px 8px', flexShrink: 0 }}>
                                        🗑
                                    </button>

                                    <span style={{ color: '#94a3b8', fontSize: 18, flexShrink: 0 }}>{isOpen ? '▾' : '▸'}</span>
                                </div>

                                {/* Expanded Content */}
                                {isOpen && (
                                    <div style={{ padding: '0 18px 18px', borderTop: '1px solid #f1f5f9' }}>
                                        {/* Add Bed */}
                                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '14px 0 14px' }}>
                                            <input
                                                style={{ ...inputStyle, maxWidth: 200 }}
                                                value={bedInputs[ward.id] || ''}
                                                onChange={e => setBedInputs(p => ({ ...p, [ward.id]: e.target.value }))}
                                                placeholder="Bed number e.g. A-101"
                                                onKeyDown={e => e.key === 'Enter' && addBed(ward.id)} />
                                            <button onClick={() => addBed(ward.id)} disabled={saving}
                                                style={{ padding: '8px 16px', background: '#0369a1', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                                                + Add Bed
                                            </button>
                                        </div>

                                        {/* Bed Grid */}
                                        {wBeds.length === 0 ? (
                                            <div style={{ textAlign: 'center', padding: '20px 0', color: '#94a3b8', fontSize: 13 }}>No beds yet — add one above</div>
                                        ) : (
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 }}>
                                                {wBeds.map(bed => (
                                                    <div key={bed.id} style={{ background: BED_STATUS_BG[bed.status] || '#f8fafc', border: `1px solid ${BED_STATUS_COLOR[bed.status] || '#e2e8f0'}40`, borderRadius: 8, padding: '10px 12px' }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                                            <span style={{ fontSize: 15, fontWeight: 800, color: '#1e293b' }}>🛏 {bed.bed_number}</span>
                                                            <span style={{ fontSize: 10, fontWeight: 700, color: BED_STATUS_COLOR[bed.status], background: BED_STATUS_COLOR[bed.status] + '18', padding: '2px 6px', borderRadius: 4 }}>
                                                                {bed.status}
                                                            </span>
                                                        </div>
                                                        {bed.status === 'Occupied' && bed.full_name ? (
                                                            <div>
                                                                <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>{bed.full_name}</div>
                                                                <div style={{ fontSize: 11, color: '#64748b' }}>{bed.mrn} · {bed.age_years}y/{bed.gender?.[0]}</div>
                                                            </div>
                                                        ) : (
                                                            <div style={{ fontSize: 11, color: '#94a3b8' }}>Empty</div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
